/**
 * System section — typed API helpers + TanStack Query hooks.
 *
 * Backend routes (per brief §6.7 + §7):
 *   GET  /v1/admin/clock                   — virtual clock state (admin variant)
 *   POST /v1/admin/clock/set               — { iso }
 *   POST /v1/admin/clock/speed             — { speed }
 *   POST /v1/admin/clock/advance           — { ms? hours? days? to_next_event? }
 *   POST /v1/admin/clock/reset             — (no body)
 *   GET  /v1/admin/health                  — backend + database health
 *   GET  /v1/admin/jobs                    — { data: JobRow[] }
 *   POST /v1/admin/jobs/:name/run-now      — 202 { run_id, job, status }
 *   GET  /v1/admin/audit-log?...           — { data: AuditLogRow[], meta }
 *   GET  /v1/admin/audit-log/:id           — AuditLogRow
 *
 * Mutations invalidate the queries they touch so the UI auto-refreshes;
 * the audit log query is invalidated after every clock mutation + every
 * run-now so newly written rows surface without a full page reload.
 */

import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import { api } from '@/api/client';

// ---------------- Shared list-response shape -----------------

export interface ListMeta {
  count: number;
  total: number;
  limit: number;
  offset: number;
}

export interface ListResponse<T> {
  data: T[];
  meta: ListMeta;
}

// ---------------- Health -----------------

export interface HealthResponse {
  status: 'ok' | 'degraded' | string;
  api?: 'online' | string;
  db?: 'connected' | 'disconnected' | string;
  timestamp?: string;
  error?: string;
}

export function useHealth() {
  return useQuery({
    queryKey: ['admin', 'health'],
    queryFn: () => api.get<HealthResponse>('/v1/admin/health'),
    refetchInterval: 30_000,
  });
}

// ---------------- Clock -----------------

export interface ClockState {
  server_time: string;
  real_time: string;
  offset_ms: number;
  speed: number;
  simulation_mode: 'off' | 'manual' | 'auto' | string;
  next_event: { at: string; label: string; round_id?: string | null } | null;
}

export function useAdminClock() {
  return useQuery({
    queryKey: ['admin', 'clock'],
    queryFn: () => api.get<ClockState>('/v1/admin/clock'),
    refetchInterval: 5000,
  });
}

export function useSetClock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (iso: string) =>
      api.post<ClockState>('/v1/admin/clock/set', { iso }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'clock'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
    },
  });
}

export function useSetClockSpeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (speed: number) =>
      api.post<ClockState>('/v1/admin/clock/speed', { speed }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'clock'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
    },
  });
}

export interface AdvanceClockBody {
  ms?: number;
  hours?: number;
  days?: number;
  to_next_event?: boolean;
}

export function useAdvanceClock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdvanceClockBody) =>
      api.post<ClockState>('/v1/admin/clock/advance', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'clock'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
    },
  });
}

export function useResetClock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ClockState>('/v1/admin/clock/reset', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'clock'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
    },
  });
}

// ---------------- Background jobs -----------------

export interface JobLastRun {
  id: string;
  status: 'success' | 'failed' | 'running' | string;
  started_at: string;
  finished_at: string | null;
  triggered_by: 'schedule' | 'admin' | string;
  error: string | null;
}

export interface JobRow {
  name: string;
  schedule: string;
  description: string;
  enabled: boolean;
  /** API configuration, not evidence that the separate worker registered it. */
  schedule_enabled?: boolean;
  last_run: JobLastRun | null;
  next_run_at: string | null;
}

export interface CronWorkerStatus {
  status: 'online' | 'stale' | 'offline' | 'unknown' | string;
  label: string;
  worker_id: string;
  last_seen_at: string | null;
  age_seconds: number | null;
  simulation_mode: string | null;
  pid: number | null;
  registered_jobs: unknown[];
  message: string;
}

export function useJobs() {
  return useQuery({
    queryKey: ['admin', 'jobs'],
    queryFn: () =>
      api.get<{
        data: JobRow[];
        meta: { count: number; simulation_mode?: string; cron_worker?: CronWorkerStatus };
      }>('/v1/admin/jobs'),
    refetchInterval: 15_000,
  });
}

export interface JobStatusResponse {
  observed_at: string;
  workers: CronWorkerStatus[] | null;
  provider_refreshes: { provider: string; season: string; data_kind: string; event_key: string; last_status: string; last_attempt_started_at: string | null; last_successful_refresh_at: string | null }[] | null;
  fixture_coverage: { game_key: string | null; status: string; count: number; with_stats: number }[] | null;
  round_scoring: { status: string; count: number; last_scored_at: string | null }[] | null;
  unavailable: string[];
}

/** Optional additive telemetry; older production keeps the jobs-only view useful. */
export function useJobStatus() {
  return useQuery({
    queryKey: ['admin', 'job-status'],
    queryFn: () => api.get<JobStatusResponse>('/v1/admin/job-status'),
    refetchInterval: 30_000,
    retry: false,
  });
}

export interface RunNowResponse {
  run_id: string;
  job: string;
  status: 'success' | 'failed' | string;
}

export function useRunJobNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api.post<RunNowResponse>(`/v1/admin/jobs/${encodeURIComponent(name)}/run-now`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'jobs'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
    },
  });
}

// ---------------- Audit log -----------------

export interface AuditLogRow {
  id: string;
  actor_id: string | null;
  actor_email: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  details: unknown;
  created_at: string;
}

export interface AuditLogFilters {
  actor?: string;
  action?: string;
  from?: string;
  to?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export function useAuditLog(filters: AuditLogFilters) {
  return useQuery({
    queryKey: ['admin', 'audit-log', filters],
    queryFn: () =>
      api.get<ListResponse<AuditLogRow>>(buildPath('/v1/admin/audit-log', filters)),
    placeholderData: keepPreviousData,
  });
}

export function useAuditLogEntry(id: string | null | undefined) {
  return useQuery({
    queryKey: ['admin', 'audit-log', id],
    queryFn: () => api.get<AuditLogRow>(`/v1/admin/audit-log/${encodeURIComponent(id ?? '')}`),
    enabled: Boolean(id),
  });
}

// ---------------- helpers -----------------

function buildPath(base: string, filters: object): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters as Record<string, unknown>)) {
    if (v === undefined || v === null || v === '' || v === 'any') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/** True when the current admin build is targeting the real production API. */
export function isProduction(): boolean {
  const env = (import.meta.env.VITE_ENV as string | undefined)?.toLowerCase();
  const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
  return env === 'production' || apiUrl === 'https://api.fantopy.ai';
}
